import React, { useEffect, useState } from 'react';
import { useRouteMatch, Link } from 'react-router-dom';
import { map, get } from 'lodash';
import scrollIntoView from 'scroll-into-view';
import BoardDetail from 'components/base/BoardDetail';
import { showConfirm } from 'components/modal/showConfirm';
import {
  getIntroductionQuestion,
  getIntroductionQuestions,
  createIntroductionQuestion,
  updateIntroductionQuestion,
  deleteIntroductionQuestion,
} from 'libs/api/main';
import { usePermission, usePagination, useDataApi } from 'libs/hooks';
import { showNotification } from 'libs/utils';
import { TBoardDetail } from 'modules/board/types';
import { TIntroductionQuestion } from 'modules/main';
import { TListResponse, TPagination, RouteMatch } from 'modules/types';
import { CWindowWidth, CQuestionCategory } from 'constants/base.const';
import { ENotificationType, EOperation, EQuestionCategory } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Col, Row, Typography, Collapse, Tag, Button, Spin, BackTop } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { PrimaryColor } from 'GlobalStyles';

const { Title } = Typography;
const IntroductionQuestionWrapper = styled.div`
  min-height: 200px;
  padding: 0px 10px;
  margin-bottom: 70px;

  .collapse-inline {
    max-width: 600px;
    width: 100%;
    margin: auto;
  }

  .ant-collapse-content-box {
    width: 100%;
    padding: 15px 10px;
    border-radius: 8px;
    background-color: #f0f8f6;
    overflow-x: hidden;
    color: #000;
    img {
      max-width: 100%;
      height: auto;
    }
  }

  .ant-tag {
    background-color: #f0f8f6 !important;
    border-radius: 8px !important;
    color: ${PrimaryColor} !important;
    border-color: transparent !important;
    margin-bottom: 6px;
  }
`;

const categories = [
  {
    label: CQuestionCategory[EQuestionCategory.SignIn],
    value: EQuestionCategory.SignIn,
  },
  {
    label: CQuestionCategory[EQuestionCategory.Mentoring],
    value: EQuestionCategory.Mentoring,
  },
  {
    label: CQuestionCategory[EQuestionCategory.Program],
    value: EQuestionCategory.Program,
  },
  {
    label: CQuestionCategory[EQuestionCategory.Content],
    value: EQuestionCategory.Content,
  },
  {
    label: CQuestionCategory[EQuestionCategory.Voucher],
    value: EQuestionCategory.Voucher,
  },
  {
    label: CQuestionCategory[EQuestionCategory.Tech],
    value: EQuestionCategory.Tech,
  },
  {
    label: CQuestionCategory[EQuestionCategory.ETC],
    value: EQuestionCategory.ETC,
  },
];

const IntroductionQuestion = () => {
  const pageSize = 7;
  const [pagination, setPagination] = usePagination(pageSize);
  const [nextPage, setNextPage] = useState(2);
  const { adminPermission } = usePermission();
  const [listQuestions, setListQuestions] = useState<JSX.Element[]>([]);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromise = getIntroductionQuestions.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      category: pagination.category,
    },
  });

  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TIntroductionQuestion>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  async function onClickDelete(id: number) {
    try {
      await deleteIntroductionQuestion(id);
      showNotification(ENotificationType.Success, '게시글이 삭제되었습니다.');
      reloadPage({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });
    } catch (e) {
      throw e;
    }
  }

  useEffect(() => {
    if (!loading) {
      const firstElemId = `newpage--${nextPage}`;
      setListQuestions([
        ...listQuestions,
        <div id={firstElemId}></div>,
        ...map(get(data, 'contents'), (content: TIntroductionQuestion, index: number) => (
          <Collapse.Panel
            header={Question(get(content, 'category') as EQuestionCategory, get(content, 'title'))}
            key={`${index}`}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: `${get(content, 'body', '')}`,
              }}
            />
            {adminPermission && (
              <Col>
                <Button
                  type='primary'
                  onClick={() =>
                    showConfirm(() => onClickDelete(get(content, 'id')), '게시글을 삭제하시겠습니까?')
                  }
                  style={{ marginRight: '20px' }}
                >
                  삭제
                </Button>
                <Link to={`${ERoute.IntroductionQuestion}/${EOperation.Edit}/${get(content, 'id')}`}>
                  <Button type='primary'>수정</Button>
                </Link>
              </Col>
            )}
          </Collapse.Panel>
        )),
      ]);
    }
  }, [loading]);

  useEffect(() => {
    if (nextPage > 2) {
      // 새로 추가된 목록들이 중앙에 오도록 scroll
      const firstElemId = `newpage--${nextPage}`;
      const elem = document?.getElementById(firstElemId) as HTMLElement;
      scrollIntoView(elem, {
        align: {
          top: 0.2,
        },
      });
    }
  }, [listQuestions]);

  const Question = (tag: EQuestionCategory, title: string) => {
    return (
      <div>
        <Tag> {CQuestionCategory[tag]} </Tag>
        <div> {title} </div>
      </div>
    );
  };

  const QuestionsList = () => {
    return (
      <Collapse
        expandIconPosition='right'
        ghost
        accordion={true}
        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
        className='collapse-inline'
      >
        {listQuestions}
      </Collapse>
    );
  };

  return (
    <IntroductionQuestionWrapper>
      <Row
        style={{
          maxWidth: `${CWindowWidth - 300}px`,
          margin: '20px auto',
          padding: '0px',
        }}
        gutter={[0, 16]}
      >
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <Title level={3} className='title'>
            자주묻는 질문
          </Title>
          <Tag
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setListQuestions([]);
              reloadPage({
                current: 1,
                pageSize: pagination.pageSize,
                category: undefined,
              });
            }}
          >
            전체
          </Tag>
          {map(categories, (v) => (
            <Tag
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setListQuestions([]);
                reloadPage({
                  current: 1,
                  pageSize: pagination.pageSize,
                  category: v.value,
                });
              }}
            >
              {CQuestionCategory[v.value]}
            </Tag>
          ))}
          {adminPermission && (
            <div style={{ float: 'left' }}>
              <Link to={`${ERoute.IntroductionQuestion}/${EOperation.Create}`}>
                <Button className='add-button' type='primary'>
                  글쓰기
                </Button>
              </Link>
            </div>
          )}
        </Col>
        <Col xs={24} sm={24} md={18} lg={18} xl={18}>
          <Spin tip='loading...' spinning={loading} delay={1000}>
            <QuestionsList />
            {!get(data, 'last') ? (
              <Button
                onClick={() => {
                  reloadPage({ current: nextPage, pageSize: pageSize });
                  setNextPage(nextPage + 1);
                }}
                style={{ margin: '10px auto', display: 'block' }}
              >
                더보기
              </Button>
            ) : (
              <div></div>
            )}
          </Spin>
        </Col>
      </Row>
      <BackTop></BackTop>
    </IntroductionQuestionWrapper>
  );
};

export const IntroductionQuestionDetail = () => {
  const match = useRouteMatch(`${ERoute.IntroductionQuestion}/:operation/:record_id?`);
  let { operation = EOperation.View, record_id } = (match?.params as RouteMatch) || {};
  const [{ data, loading }] = useDataApi<TBoardDetail>(
    getIntroductionQuestion.bind(null, record_id),
    {},
    operation != EOperation.Create,
  );

  return (
    <BoardDetail
      operation={operation}
      pathName={ERoute.IntroductionQuestion}
      promiseCreate={createIntroductionQuestion}
      promiseDelete={deleteIntroductionQuestion}
      promiseUpdate={updateIntroductionQuestion}
      record={data}
      categories={categories}
      loading={loading}
      useFile={false}
      useThumbnail={false}
    />
  );
};

export default IntroductionQuestion;
